import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth, hasRole } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, FileUp, Search, Users2, Info, Pencil } from "lucide-react";
import StateDistrictSelect from "@/components/StateDistrictSelect";

const emptyForm = {
  party_name: "", gst: "", address: "", city: "", state: "", district: "", pincode: "",
  contact_person: "", mobile: "", email: "", category: "",
  assigned_distributor_id: "", assigned_employee_id: "", assigned_ibo_id: "",
  management_type: "Company Direct",
};

const MGMT_TYPES = [
  "Distributor Only", "Distributor + Market Developer", "Distributor + IBO",
  "Company Direct", "Company + IBO",
];

// IBOs are restricted to two management types when they create parties:
// - "Direct IBO" (stored as "Company + IBO") — IBO owns the party end-to-end.
// - "Distributor + IBO" — Admin will assign a distributor after creation.
const IBO_MGMT_OPTIONS = [
  { label: "Direct IBO", value: "Company + IBO" },
  { label: "Distributor + IBO", value: "Distributor + IBO" },
];

// Market Developers (market_developer / sales) get the same treatment as IBOs:
// - "Direct Market Developer" (stored as "Company Direct") — MD owns the party end-to-end.
// - "Distributor + Market Developer" — Admin will assign a distributor after creation.
const EMPLOYEE_MGMT_OPTIONS = [
  { label: "Direct Market Developer", value: "Company Direct" },
  { label: "Distributor + Market Developer", value: "Distributor + Market Developer" },
];

const REG_COLOR = {
  Registered: "bg-green-100 text-green-800",
  "Not Registered": "bg-amber-100 text-amber-800",
};

export default function Parties() {
  const { user } = useAuth();
  const isAdmin = hasRole(user, "admin");
  const isIBO = hasRole(user, "ibo") && !isAdmin;
  const isEmployee = hasRole(user, "market_developer", "sales") && !isAdmin && !isIBO;
  const isDistributor = hasRole(user, "distributor") && !isAdmin;
  const canCreate = hasRole(user, "admin", "backend_executive", "distributor", "ibo", "market_developer", "sales");
  const canRegister = hasRole(user, "admin", "accountant");
  const canBulk = hasRole(user, "admin", "backend_executive");
  const [parties, setParties] = useState([]);
  const [distributors, setDistributors] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [ibos, setIbos] = useState([]);
  const [regFilter, setRegFilter] = useState("all");
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get("q") || "");
  useEffect(() => {
    const urlQ = searchParams.get("q");
    if (urlQ) setSearch(urlQ);
  }, [searchParams]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkTarget, setBulkTarget] = useState("");
  const [bulkOpen, setBulkOpen] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);  // when set → dialog is in edit mode

  const [regOpen, setRegOpen] = useState(false);
  const [regTarget, setRegTarget] = useState(null);
  const [regForm, setRegForm] = useState({ invoice_type: "Proforma", invoice_number: "", invoice_amount: 0, file: null });

  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/distribution/parties");
      setParties(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  const loadRefs = useCallback(async () => {
    try {
      const { data } = await api.get("/distribution/distributors");
      setDistributors(data);
    } catch { /* ignore */ }
    try {
      const { data } = await api.get("/users");
      setEmployees((data || []).filter((u) => {
        const r = Array.isArray(u.roles) && u.roles.length ? u.roles : [u.role];
        return r.some((x) => ["sales", "market_developer", "backend_executive"].includes(x));
      }));
      setIbos((data || []).filter((u) => {
        const r = Array.isArray(u.roles) && u.roles.length ? u.roles : [u.role];
        return r.includes("ibo");
      }));
    } catch { /* non-admin can't fetch users */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadRefs(); }, [loadRefs]);

  const distMap = useMemo(() => Object.fromEntries(distributors.map((d) => [d.id, d.distributor_name])), [distributors]);
  const iboMap = useMemo(() => Object.fromEntries(ibos.map((u) => [u.id, u.name])), [ibos]);
  const mdMap = useMemo(() => Object.fromEntries(employees.map((u) => [u.id, u.name])), [employees]);

  // Pretty-print who created this party. Falls back to just the role if the
  // name wasn't stamped (legacy rows).
  const createdByLabel = (p) => {
    const roleMap = {
      admin: "Admin", accountant: "Accountant", backend_executive: "Backend",
      market_developer: "Market Developer", sales: "Sales", operations: "Operations",
      approval: "Approval", distributor: "Distributor", ibo: "IBO",
    };
    const roleLabel = roleMap[p.created_by_role] || p.created_by_role || "—";
    const name = p.created_by_name;
    if (!p.created_by_role) return name || "—";
    return name ? `${roleLabel} — ${name}` : roleLabel;
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return parties.filter((p) => {
      if (regFilter !== "all" && p.registration_status !== regFilter) return false;
      if (!q) return true;
      return (
        (p.party_name || "").toLowerCase().includes(q) ||
        (p.party_code || "").toLowerCase().includes(q) ||
        (p.gst || "").toLowerCase().includes(q) ||
        (p.city || "").toLowerCase().includes(q) ||
        (p.mobile || "").toLowerCase().includes(q)
      );
    });
  }, [parties, search, regFilter]);

  const toggleSelect = (id) => {
    setSelectedIds((cur) => cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
  };

  const openCreate = () => {
    // Scope the default management_type per role. Neither IBOs, MDs, nor
    // distributors see the picker — the admin edits mgmt_type / assigns
    // people later. Backend also enforces these defaults defensively.
    let scopedForm = emptyForm;
    if (isIBO) scopedForm = { ...emptyForm, management_type: "Company + IBO" };
    else if (isEmployee) scopedForm = { ...emptyForm, management_type: "Company Direct" };
    else if (isDistributor) scopedForm = { ...emptyForm, management_type: "Distributor Only" };
    setForm(scopedForm);
    setEditingId(null);
    setCreateOpen(true);
  };

  // Admin/backend_executive can edit an existing party — pre-fills every field
  // from the row so they can change management_type, reassign IBO/MD, etc.
  const openEdit = (p) => {
    setForm({
      party_name: p.party_name || "",
      gst: p.gst || "",
      address: p.address || "",
      city: p.city || "",
      state: p.state || "",
      district: p.district || "",
      pincode: p.pincode || "",
      contact_person: p.contact_person || "",
      mobile: p.mobile || "",
      email: p.email || "",
      category: p.category || "",
      assigned_distributor_id: p.assigned_distributor_id || "",
      assigned_employee_id: p.assigned_employee_id || "",
      assigned_ibo_id: p.assigned_ibo_id || "",
      management_type: p.management_type || "Company Direct",
    });
    setEditingId(p.id);
    setCreateOpen(true);
  };
  const openRegister = (p) => {
    setRegTarget(p);
    setRegForm({
      invoice_type: p.invoice_type || "Proforma",
      invoice_number: p.invoice_number || "",
      invoice_amount: p.invoice_amount || 0,
      file: null,
    });
    setRegOpen(true);
  };

  const create = async () => {
    if (!form.party_name.trim()) return toast.error("Party name is required");
    if (!form.state) return toast.error("State is required");
    if (!form.district) return toast.error("District is required");
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      if (editingId) {
        await api.put(`/distribution/parties/${editingId}`, payload);
        toast.success("Party updated");
      } else {
        await api.post("/distribution/parties", payload);
        toast.success("Party created");
      }
      setCreateOpen(false);
      setEditingId(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const register = async () => {
    if (!regTarget) return;
    if (!regForm.invoice_number.trim()) return toast.error("Invoice number required");
    try {
      const fd = new FormData();
      if (regForm.file) fd.append("file", regForm.file);
      fd.append("invoice_type", regForm.invoice_type);
      fd.append("invoice_number", regForm.invoice_number.trim());
      fd.append("invoice_amount", String(Number(regForm.invoice_amount) || 0));
      await api.post(`/distribution/parties/${regTarget.id}/register`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Party registered (${regForm.invoice_type} invoice)`);
      setRegOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const doBulk = async () => {
    if (!bulkTarget) return toast.error("Choose a distributor");
    if (selectedIds.length === 0) return toast.error("Select at least one party");
    try {
      const { data } = await api.post("/distribution/parties/bulk-assign", {
        party_ids: selectedIds,
        distributor_id: bulkTarget,
      });
      toast.success(`Assigned ${data.modified} parties`);
      setBulkOpen(false);
      setSelectedIds([]);
      setBulkTarget("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h1 className="font-display font-bold text-2xl tracking-tight">Parties</h1>
          <p className="text-sm text-muted-foreground">Anyone authorized can create a party. Accountant registers by uploading a Proforma or Tax invoice.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-3 text-muted-foreground" />
            <Input
              placeholder="Search name, code, GST, city, mobile"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 w-64"
              data-testid="dm-party-search"
            />
          </div>
          <Select value={regFilter} onValueChange={setRegFilter}>
            <SelectTrigger className="w-44" data-testid="dm-party-reg-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All parties</SelectItem>
              <SelectItem value="Registered">Registered</SelectItem>
              <SelectItem value="Not Registered">Not Registered</SelectItem>
            </SelectContent>
          </Select>
          {canBulk && selectedIds.length > 0 && (
            <Button variant="outline" onClick={() => setBulkOpen(true)} data-testid="dm-bulk-assign-btn">
              <Users2 className="w-4 h-4 mr-1" /> Assign {selectedIds.length}
            </Button>
          )}
          {canCreate && <Button onClick={openCreate} data-testid="dm-add-party-btn"><Plus className="w-4 h-4 mr-1" /> Add Party</Button>}
        </div>
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {canBulk && <TableHead className="w-8"></TableHead>}
                <TableHead>Code</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>GST</TableHead>
                <TableHead>City</TableHead>
                <TableHead>State</TableHead>
                <TableHead>Distributor</TableHead>
                <TableHead>Assigned IBO</TableHead>
                <TableHead>Assigned MD</TableHead>
                <TableHead>Mgmt Type</TableHead>
                <TableHead>Created By</TableHead>
                <TableHead>Registration</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow><TableCell colSpan={14} className="text-center text-muted-foreground py-8" data-testid="dm-parties-empty">No parties match.</TableCell></TableRow>
              )}
              {filtered.map((p) => (
                <TableRow key={p.id} data-testid={`dm-party-row-${p.id}`}>
                  {canBulk && (
                    <TableCell>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(p.id)}
                        onChange={() => toggleSelect(p.id)}
                        data-testid={`dm-party-checkbox-${p.id}`}
                      />
                    </TableCell>
                  )}
                  <TableCell className="font-mono text-xs">{p.party_code || <span className="text-muted-foreground">—</span>}</TableCell>
                  <TableCell className="font-medium">{p.party_name}</TableCell>
                  <TableCell className="text-xs">{p.gst || "—"}</TableCell>
                  <TableCell>{p.city || "—"}</TableCell>
                  <TableCell>{p.state || "—"}</TableCell>
                  <TableCell className="text-xs">{distMap[p.assigned_distributor_id] || "—"}</TableCell>
                  <TableCell className="text-xs">{iboMap[p.assigned_ibo_id] || <span className="text-muted-foreground">—</span>}</TableCell>
                  <TableCell className="text-xs">{mdMap[p.assigned_employee_id] || <span className="text-muted-foreground">—</span>}</TableCell>
                  <TableCell className="text-xs">{p.management_type}</TableCell>
                  <TableCell className="text-xs" data-testid={`dm-party-created-by-${p.id}`}>{createdByLabel(p)}</TableCell>
                  <TableCell>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${REG_COLOR[p.registration_status] || "bg-gray-100"}`}>
                      {p.registration_status}
                    </span>
                    {p.invoice_type && (
                      <span className="ml-1 text-[10px] text-muted-foreground">({p.invoice_type})</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => setDetail(p)} data-testid={`dm-party-detail-${p.id}`}>
                      <Info className="w-4 h-4" />
                    </Button>
                    {canBulk && (
                      <Button variant="ghost" size="sm" onClick={() => openEdit(p)} data-testid={`dm-edit-party-${p.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                    )}
                    {canRegister && (
                      <Button variant="outline" size="sm" onClick={() => openRegister(p)} data-testid={`dm-register-party-${p.id}`}>
                        <FileUp className="w-4 h-4 mr-1" />
                        {p.registration_status === "Registered" ? "Replace" : "Register"}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={(v) => { setCreateOpen(v); if (!v) setEditingId(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingId ? "Edit Party" : "New Party"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1 md:col-span-2"><Label>Party Name *</Label><Input value={form.party_name} onChange={(e) => setForm({ ...form, party_name: e.target.value })} data-testid="dm-party-name" /></div>
            <div className="space-y-1"><Label>GST</Label><Input value={form.gst} onChange={(e) => setForm({ ...form, gst: e.target.value })} /></div>
            <div className="space-y-1"><Label>Category</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></div>
            <div className="space-y-1 md:col-span-2"><Label>Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <div className="md:col-span-2">
              <StateDistrictSelect
                state={form.state}
                district={form.district}
                onStateChange={(v) => setForm({ ...form, state: v })}
                onDistrictChange={(v) => setForm({ ...form, district: v })}
                testidPrefix="dm-party"
                required
              />
            </div>
            <div className="space-y-1"><Label>City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
            <div className="space-y-1"><Label>Pincode</Label><Input value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} /></div>
            <div className="space-y-1"><Label>Contact Person</Label><Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} /></div>
            <div className="space-y-1"><Label>Mobile</Label><Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} /></div>
            <div className="space-y-1"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            {isDistributor ? (
              <div className="space-y-1 md:col-span-2">
                <p className="text-xs text-blue-800 bg-blue-50 border border-blue-200 rounded px-2 py-1" data-testid="distributor-party-notice">
                  This party will be created under your distributor account. Admin may later assign an IBO / Market Developer.
                </p>
              </div>
            ) : (
              <div className="space-y-1 md:col-span-2">
                <Label>Management Type *</Label>
                {isIBO || isEmployee ? (
                  <Select value={form.management_type} onValueChange={(v) => setForm({ ...form, management_type: v })}>
                    <SelectTrigger data-testid="dm-party-mgmt"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(isIBO ? IBO_MGMT_OPTIONS : EMPLOYEE_MGMT_OPTIONS).map((t) => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Select value={form.management_type} onValueChange={(v) => setForm({ ...form, management_type: v })}>
                    <SelectTrigger data-testid="dm-party-mgmt"><SelectValue /></SelectTrigger>
                    <SelectContent>{MGMT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                )}
                {(isIBO && form.management_type === "Distributor + IBO") && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1" data-testid="ibo-dist-assign-notice">
                    A distributor will be assigned by the admin after this party is created.
                  </p>
                )}
                {(isEmployee && form.management_type === "Distributor + Market Developer") && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1" data-testid="md-dist-assign-notice">
                    A distributor will be assigned by the admin after this party is created.
                  </p>
                )}
              </div>
            )}
            {!hasRole(user, "distributor") && !isIBO && !isEmployee && (
              <div className="space-y-1 md:col-span-2">
                <Label>Assigned Distributor <span className="text-xs text-muted-foreground">(optional)</span></Label>
                <Select value={form.assigned_distributor_id || "__none__"} onValueChange={(v) => setForm({ ...form, assigned_distributor_id: v === "__none__" ? "" : v })}>
                  <SelectTrigger data-testid="dm-party-distributor"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None</SelectItem>
                    {distributors.map((d) => <SelectItem key={d.id} value={d.id}>{d.distributor_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {!isIBO && !isEmployee && (
              <>
                <div className="space-y-1">
                  <Label>Assigned Market Developer</Label>
                  <Select value={form.assigned_employee_id || "__none__"} onValueChange={(v) => setForm({ ...form, assigned_employee_id: v === "__none__" ? "" : v })}>
                    <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">None</SelectItem>
                      {employees.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Assigned IBO</Label>
                  <Select value={form.assigned_ibo_id || "__none__"} onValueChange={(v) => setForm({ ...form, assigned_ibo_id: v === "__none__" ? "" : v })}>
                    <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">None</SelectItem>
                      {ibos.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={create} data-testid="dm-save-party">{editingId ? "Save Changes" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Register dialog */}
      <Dialog open={regOpen} onOpenChange={setRegOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Register Party</DialogTitle></DialogHeader>
          {regTarget && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Uploading an invoice will register <b>{regTarget.party_name}</b> with the company and assign a party code.</p>
              <div className="flex gap-2">
                {["Proforma", "Tax"].map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setRegForm({ ...regForm, invoice_type: t })}
                    className={`px-3 py-1 rounded-full text-xs font-medium border ${regForm.invoice_type === t ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}
                    data-testid={`dm-register-type-${t.toLowerCase()}`}
                  >
                    {t} Invoice
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label>Invoice Number *</Label><Input value={regForm.invoice_number} onChange={(e) => setRegForm({ ...regForm, invoice_number: e.target.value })} data-testid="dm-register-invnum" /></div>
                <div className="space-y-1"><Label>Amount</Label><Input type="number" value={regForm.invoice_amount} onChange={(e) => setRegForm({ ...regForm, invoice_amount: e.target.value })} data-testid="dm-register-amount" /></div>
              </div>
              <div className="space-y-1">
                <Label>Invoice PDF <span className="text-xs text-muted-foreground">(optional)</span></Label>
                <Input type="file" accept="application/pdf" onChange={(e) => setRegForm({ ...regForm, file: e.target.files?.[0] || null })} data-testid="dm-register-file" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRegOpen(false)}>Cancel</Button>
            <Button onClick={register} data-testid="dm-register-submit">Register</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk assign dialog */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Assign {selectedIds.length} parties to a distributor</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label>Distributor</Label>
            <Select value={bulkTarget} onValueChange={setBulkTarget}>
              <SelectTrigger data-testid="dm-bulk-distributor"><SelectValue placeholder="Select distributor..." /></SelectTrigger>
              <SelectContent>
                {distributors.map((d) => <SelectItem key={d.id} value={d.id}>{d.distributor_name} ({d.distributor_code})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)}>Cancel</Button>
            <Button onClick={doBulk} data-testid="dm-bulk-submit">Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{detail?.party_name}</DialogTitle></DialogHeader>
          {detail && (
            <div className="text-sm space-y-2">
              <Row k="Code" v={detail.party_code || "—"} />
              <Row k="Registration" v={<span className={`px-2 py-0.5 rounded-full text-xs font-medium ${REG_COLOR[detail.registration_status] || "bg-gray-100"}`}>{detail.registration_status}</span>} />
              {detail.invoice_type && <Row k="Invoice" v={`${detail.invoice_type} · ${detail.invoice_number} · ₹${Number(detail.invoice_amount || 0).toLocaleString("en-IN")}`} />}
              <div className="pt-2 border-t border-border">
                <div className="text-xs font-semibold text-muted-foreground mb-1">Audit trail</div>
                <Row k="Created by" v={`${detail.created_by_name || "—"} · ${detail.created_at ? new Date(detail.created_at).toLocaleString() : "—"}`} />
                <Row k="Updated by" v={`${detail.updated_by_name || "—"} · ${detail.updated_at ? new Date(detail.updated_at).toLocaleString() : "—"}`} />
                <Row k="Registered by" v={detail.registered_by_name ? `${detail.registered_by_name} · ${new Date(detail.registration_date).toLocaleString()}` : "—"} />
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setDetail(null)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-4">
      <div className="text-xs text-muted-foreground">{k}</div>
      <div className="text-right">{v}</div>
    </div>
  );
}
